import * as React from 'react';
import { ForecastTimelineChart } from '@/components/charts';
import { useForecastRange } from '@/hooks/api';
import type { StormImpactModel } from '@/types';

// Callback-ref approach: observer is set up whenever the element mounts/unmounts,
// regardless of conditional rendering or early returns.
function useContainerHeight(): [React.RefCallback<HTMLDivElement>, number] {
  const [height, setHeight] = React.useState(0);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  const ref = React.useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        setHeight(entries[0].contentRect.height);
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  return [ref, height];
}

interface TimelineTileProps {
  nodeId?: string;
  modelId?: string;
  weatherModelId?: string;
  startDate?: Date;
  forecastRange?: number;
  run?: string;
  stormImpactModel?: StormImpactModel;
  units?: string;
}

export function TimelineTile({
  nodeId,
  modelId,
  weatherModelId,
  startDate,
  forecastRange = 5,
  run,
  stormImpactModel,
  units = 'Customers',
}: TimelineTileProps) {
  const [chartContainerRef, chartHeight] = useContainerHeight();
  const hasEverHadDataRef = React.useRef(false);
  const allParamsPresent = !!nodeId && !!modelId && !!weatherModelId && !!startDate && !!run;
  const isTransitioning = hasEverHadDataRef.current && !allParamsPresent;

  const { data, loading, error, isPlaceholderData } = useForecastRange({
    nodeId,
    modelId,
    weatherModelId,
    startDate,
    forecastRange,
    run,
    stormImpactModel,
  });

  if (data.length > 0) hasEverHadDataRef.current = true;
  const isRefreshing = loading || isTransitioning || isPlaceholderData;

  if (!allParamsPresent && !hasEverHadDataRef.current) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-xs text-muted-foreground">Select a location to view the forecast timeline.</p>
      </div>
    );
  }

  return (
    <div ref={chartContainerRef} className="relative h-full">
      {isRefreshing && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/80 backdrop-blur-[1px]">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
            <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-200" />
            <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-400" />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Loading forecast timeline...</p>
        </div>
      )}

      {!isRefreshing && (error || data.length === 0) && (
        <div className="flex h-full flex-col items-center justify-center p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <span className="text-xl">📊</span>
          </div>
          <h3 className="text-sm font-medium mb-2">No Forecast Data Available</h3>
          <p className="text-xs text-muted-foreground">No forecast data found for the selected date range.</p>
        </div>
      )}

      {!isPlaceholderData && data.length > 0 && (
        <div
          className={`absolute inset-0 p-2 transition-opacity duration-150 ${isRefreshing ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden={isRefreshing}
        >
          {chartHeight > 0 && (
            <ForecastTimelineChart
              data={data}
              height={chartHeight - 16}
              yAxisLabel={units}
              showGrid={true}
              showTooltip={true}
              showThreats={true}
              className="w-full"
            />
          )}
        </div>
      )}
    </div>
  );
}
